import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { components } from './api-schema';
import { call } from './api';
import { normCode } from './normCode';

type Costs = components['schemas']['DirectCosts'];
type Snapshot = components['schemas']['DirectCostSnapshot'];
const number = (value: string | null | undefined, digits = 2) => value == null ? '—'
  : Number(value).toLocaleString('ru-RU', { maximumFractionDigits: digits });
const date = (value: string) => new Date(value).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
const names: Record<string,string> = { labor: 'Оплата труда', machines: 'Эксплуатация машин', materials: 'Материалы' };

export function EstimateCalculation({ revisionId, canEdit, openWork }: {
  revisionId: string; canEdit: boolean; openWork?: (id: string) => void;
}) {
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all'|'incomplete'>('all');
  const [query, setQuery] = useState('');
  useEffect(() => { setSelected(''); setError(''); setFilter('all'); setQuery(''); }, [revisionId]);
  const current = useQuery({ queryKey:['direct-costs',revisionId],
    queryFn:()=>call<Costs>(`/api/v1/revisions/${revisionId}/direct-costs`) });
  const history = useQuery({ queryKey:['direct-cost-history',revisionId],
    queryFn:()=>call<Snapshot[]>(`/api/v1/revisions/${revisionId}/direct-costs/snapshots`) });
  const saved = useQuery({ queryKey:['direct-cost-snapshot',selected], enabled:!!selected,
    queryFn:()=>call<Costs>(`/api/v1/direct-cost-snapshots/${selected}`) });
  const data = selected ? saved.data : current.data;
  const active = selected ? saved : current;
  const save = async () => {
    if (!current.data) return;
    setBusy(true); setError('');
    try {
      const result = await call<Costs>(`/api/v1/revisions/${revisionId}/direct-costs/snapshots`, {
        method:'POST', body:{input_hash:current.data.input_hash},
      });
      await history.refetch(); setSelected(result.snapshot_id ?? '');
    } catch (cause) { setError(String(cause)); await current.refetch(); await history.refetch(); }
    finally { setBusy(false); }
  };
  const metadata = data?.rate_book?.metadata ?? {};
  const rows = (data?.rows ?? []).filter(row => (filter !== 'incomplete' || row.amount == null) &&
    `${row.description} ${row.norm_code} ${normCode(row.norm_code)} ${row.placement}`.toLocaleLowerCase().includes(query.toLocaleLowerCase().trim()));
  return <section className="review-register cost-register">
    <header>
      <h2>Прямые затраты по нормам</h2>
      <p>Объём ВОР переводится в единицу нормы и умножается на цену каждой составляющей.
        Комплексное применение учитывается один раз для всех покрываемых работ.</p>
      <p className="cost-boundary">Здесь приведены прямые затраты в уровне цен карточек.
        Полный расчёт с начислениями доступен во вкладке «Расчёт сметы».</p>
      <div className="cost-actions">
        {(history.data?.length ?? 0) > 0 && <label className="field"><span>Версия расчёта</span>
          <select value={selected} disabled={busy} onChange={event=>setSelected(event.target.value)}>
            <option value="">Текущие данные ВОР</option>
            {history.data?.map(row=><option value={row.id} key={row.id}>{date(row.created_at)} · {row.author_name} · {number(row.direct_total ?? row.known_subtotal)} ₽{row.direct_total == null ? ' · неполный' : ''}</option>)}
          </select>
        </label>}
        <button type="button" className="button small" disabled={busy || current.isFetching}
          onClick={()=>{ setSelected(''); void current.refetch(); }}>{selected ? 'К текущему расчёту' : 'Обновить расчёт'}</button>
        {!selected && canEdit && <button type="button" className="button primary" disabled={!data || busy || current.isFetching}
          onClick={()=>void save()}>{busy ? 'Сохраняем…' : 'Сохранить расчёт'}</button>}
        {data?.download_url && <a className="button-link primary" href={data.download_url} download>Скачать расчёт XLSX</a>}
      </div>
      {error && <p role="alert" className="error">{error}</p>}
      {history.error && <p role="alert" className="error">Не удалось загрузить историю: {String(history.error)}</p>}
      {active.error && <p role="alert" className="error">{String(active.error)}</p>}
      {active.isPending && <p role="status">Собираем объёмы и карточки затрат…</p>}
    </header>
    {data && <>
      {selected && <p className="cost-snapshot-note">Сохранённый снимок · {data.created_at ? date(data.created_at) : ''} · {data.author_name}.
        {current.data && current.data.input_hash !== data.input_hash ? ' Текущие данные изменились. Этот расчёт сохранён без пересчёта.' : ' Исходные значения и файл сохранены.'}</p>}
      {data.rate_book ? <p className="muted">{data.catalog_name} · {data.catalog_edition} · {String(metadata.region ?? '')} · {String(metadata.price_level ?? '')}</p>
        : <div className="empty cost-empty"><h3>Карточки затрат не загружены</h3><p>Нужны цены труда, машин и материалов на единицу каждой нормы с точной базой, редакцией и источником.
          Цены отдельных ресурсов не заменяют эти карточки. Объёмы и существующая ВОР сохранены.</p></div>}
      <div className="cost-summary" aria-label="Полнота расчёта">
        <div><span>Рассчитанная часть прямых затрат</span><strong>{data.counts.priced_components > 0 ? `${number(data.known_subtotal)} ₽` : 'Не определена'}</strong></div>
        <p>Полностью рассчитано строк: {data.counts.priced} из {data.counts.rows}.
          {data.direct_total == null ? ' Прямые затраты всего выбранного состава не определены.' : ' Это сумма прямых затрат всех строк, без дополнительных начислений.'}
          {data.counts.unapproved > 0 ? ` Не подтверждено работ: ${data.counts.unapproved}.` : ''}</p>
      </div>
      <details className="cost-limitations"><summary>Состав расчёта и ограничения</summary>
        <ul>{data.notes.map(note=><li key={note}>{note}</li>)}</ul>
        {data.rate_book && <p>Файл карточек: {data.rate_book.filename}. Источник: {String(metadata.source ?? '')}.
          SHA-256: <span className="source-hash">{data.rate_book.sha256}</span><br/>
          <a href={data.rate_book.download_url} download>Скачать исходные карточки затрат</a></p>}
        <p>Входные данные: <span className="source-hash">{data.input_hash}</span></p>
      </details>
      <div className="cost-filters">
        <input aria-label="Найти работу в расчёте" placeholder="Работа, тип или код нормы" value={query} onChange={event=>setQuery(event.target.value)} />
        <select aria-label="Полнота строки расчёта" value={filter} onChange={event=>setFilter(event.target.value as 'all'|'incomplete')}>
          <option value="all">Все строки · {data.counts.rows}</option><option value="incomplete">Неполные · {data.counts.unpriced}</option>
        </select>
      </div>
      <div className="cost-table-scroll"><table className="cost-table">
        <thead><tr><th>Работа / применение нормы</th><th>Объём нормы</th><th>Прямые затраты</th><th>Проверка</th></tr></thead>
        <tbody>{rows.map(row=><tr key={row.key}>
          <td><strong>{normCode(row.norm_code) || 'Норма не выбрана'}</strong><p>{row.description}</p><small className="muted">{row.placement}</small>
            <details><summary>Расчёт и основания</summary>
              <p>Объём ВОР: {number(row.natural_quantity,8)} {row.natural_unit} → {number(row.quantity,8)} {row.unit}.
                {row.quantity_origin === 'manual' ? ' Объём уточнён специалистом.' : ''}</p>
              {Object.entries(row.components).map(([key,part])=><p key={key}>
                {names[key] || key}: {number(row.quantity,8)} × {number(part.unit_price,8)} ₽ = {number(part.amount)} ₽
                {part.unit_price == null ? ' · цена не определена' : ''}</p>)}
              {row.source && <p>Основание цены: {row.source}</p>}
              {row.norm_title && <p>{row.norm_title}</p>}
              {row.work_ids.length > 1 && <p>Одно применение покрывает работ: {row.work_ids.length}. Стоимость не умножается на их количество.</p>}
              <div className="cost-source-links">{row.sources.map(source=><a key={source.url} href={source.url} target="_blank" rel="noreferrer">{source.label}</a>)}</div>
              {selected && openWork && row.covered_works.length > 0 && <p className="muted">Объёмы ниже сохранены в снимке. Ссылки на работы открывают текущую ВОР.</p>}
              {row.covered_works.length > 0 ? <ul className="cost-work-links" aria-label="Покрываемые работы и объёмы">
                {row.covered_works.map(work=><li key={work.id}>
                  {openWork ? <button type="button" className="text-button" onClick={()=>openWork(work.id)}>{work.description}</button> : work.description}
                  {' · '}{work.quantity == null ? 'Объём не определён' : `${number(work.quantity,8)} ${work.unit}`}
                  {work.id === row.quantity_work_id && <strong> · основание объёма нормы</strong>}
                </li>)}
              </ul> : <>
                {row.work_ids.length > 1 && <p className="muted">Объёмы отдельных операций в этом снимке не сохранены.</p>}
                {openWork && <div className="cost-work-links">{row.work_ids.map((id,index)=><button key={id} type="button" className="text-button"
                  onClick={()=>openWork(id)}>Открыть текущую работу{row.work_ids.length > 1 ? ` ${index+1}` : ''}</button>)}</div>}
              </>}
            </details>
          </td>
          <td className="cost-number" data-label="Объём нормы">{number(row.quantity,8)}<small>{row.unit}</small>
            <small>ВОР: {number(row.natural_quantity,8)} {row.natural_unit}</small></td>
          <td className="cost-number" data-label="Прямые затраты">{number(row.amount)}<small>₽</small></td>
          <td>{row.issues.length > 0 ? <ul>{row.issues.map(issue=><li key={issue}>{issue}</li>)}</ul> : <span className="muted">Составляющие определены</span>}
            {row.review_notes.map(note=><p className="muted" key={note}>{note}</p>)}</td>
        </tr>)}</tbody>
      </table></div>
      {rows.length === 0 && <p className="empty">Нет строк по выбранному фильтру.</p>}
    </>}
  </section>;
}
